import React, { useState, useEffect, useRef, Fragment } from 'react';
import { encountersAPI } from '../services/api';
import { getProfilePicture, getFirstName, isOnline } from '../utils/helpers';
import { FaHeart, FaTimes, FaGift, FaStar, FaMapMarkerAlt, FaInfoCircle, FaChevronDown, FaUndo, FaFire, FaCog, FaBell, FaUser, FaSignOutAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GiftModal from '../components/GiftModal';
import Ads from '../components/Ads';
import Header from '../components/Header';

const Encounters = () => {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();


  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Encounters: Logout error:', error);
    }
  };
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noEncounters, setNoEncounters] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [startX, setStartX] = useState(0);
  const cardRef = useRef(null);

  useEffect(() => {
    // Only fetch encounter when user is authenticated
    if (user && user.id) {
      fetchEncounter();
    }
  }, [user]);

  const fetchEncounter = async () => {
    try {
      setLoading(true);
      const response = await encountersAPI.getEncounter();

      if (response && response.encounter) {
        setProfile({
          ...response.encounter,
          score: response.encounter.score || { percentage: 85, likes: 1250, total: 1470 }
        });
        setNoEncounters(false);
        setShowInfo(false);
        setDragOffset(0);
      } else {
        setNoEncounters(true);
      }
    } catch (error) {
      console.error('Failed to fetch encounter:', error);
      // Set a fallback profile for testing (only in development)
      if (process.env.NODE_ENV === 'development') {
      setProfile({
        id: 1,
        full_name: 'Test User',
        age: 25,
        city: 'Test City',
        country: 'Test Country',
        score: { percentage: 85, likes: 1250, total: 1470 }
      });
      setNoEncounters(false);
      setShowInfo(false);
      setDragOffset(0);
      } else {
        setNoEncounters(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!profile) return;
    const currentProfile = profile; // Store current profile for background API call

    // Immediately fetch next profile for instant UI feedback
    fetchEncounter();

    // Make API call in background
    try {
      await encountersAPI.likeProfile(currentProfile.id);
      toast.success(`You liked ${getFirstName(currentProfile.full_name)}! ❤️`);
    } catch (error) {
      console.error('Failed to like profile:', error);
      toast.error('Failed to like profile');
    }
  };

  const handleDislike = async () => {
    if (!profile) return;
    const currentProfile = profile; // Store current profile for background API call

    // Immediately fetch next profile for instant UI feedback
    fetchEncounter();

    // Make API call in background
    try {
      await encountersAPI.dislikeProfile(currentProfile.id);
    } catch (error) {
      console.error('Failed to dislike profile:', error);
      toast.error('Failed to dislike profile');
    }
  };


  // Touch/Swipe handlers
  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    const threshold = 100;
    if (dragOffset > threshold) {
      // Swipe right - Like
      handleLike();
    } else if (dragOffset < -threshold) {
      // Swipe left - Dislike
      handleDislike();
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseDown = (e) => {
    setStartX(e.clientX);
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const diff = e.clientX - startX;
    setDragOffset(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    const threshold = 100;
    if (dragOffset > threshold) {
      handleLike();
    } else if (dragOffset < -threshold) {
      handleDislike();
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  // Loading handled in main content area

  // No encounters handled in main content area

  // if (!profile) return null; // Temporarily disabled for debugging

  // Show only profile picture
  const currentPhoto = getProfilePicture(profile);

  const cardStyle = {
    transform: `translateX(${dragOffset}px) rotate(${dragOffset * 0.1}deg)`,
    transition: isDragging ? 'none' : 'transform 0.3s ease-out',
  };

  const likeOpacity = dragOffset > 50 ? Math.min(dragOffset / 150, 1) : 0;
  const nopeOpacity = dragOffset < -50 ? Math.min(-dragOffset / 150, 1) : 0;


  return (
    <div className="min-h-screen bg-white">
      {/* Professional Header */}
      <Header title="Matching" />

      {/* Main Content */}
      <div className="matchus-main-content">
        {loading ? (
          <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl 2xl:max-w-4xl mx-auto">
            <div className="matchus-loading">
              <div className="matchus-spinner"></div>
              <h2 className="matchus-loading-text">Finding your perfect match...</h2>
              <p className="text-gray-500">Please wait while we search for compatible profiles</p>
            </div>
          </div>
        ) : noEncounters ? (
          <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl 2xl:max-w-4xl mx-auto">
            <div className="matchus-empty">
              <div className="matchus-empty-icon">💔</div>
              <h2 className="matchus-empty-title">No More Profiles</h2>
              <p className="matchus-empty-text">You've seen all available profiles for now.</p>
              <p className="text-gray-500 mb-6">Check back later for more matches!</p>
              <button
                type="button"
                className="matchus-retry-btn"
                onClick={fetchEncounter}
                disabled={loading}
              >
                <FaUndo className="inline mr-2" />
                {loading ? 'Loading...' : 'Try Again'}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl 2xl:max-w-4xl mx-auto matchus-encounter-layout">
        {/* First Row - Card */}
        <div className="matchus-card-row">
        <div className="matchus-card-container">
        <div
          ref={cardRef}
            className="matchus-card"
          style={cardStyle}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Like/No indicator */}
          <div
            className="matchus-like-indicator"
            style={{ opacity: likeOpacity }}
          >
            LIKE
          </div>
          <div
            className="matchus-nope-indicator"
            style={{ opacity: nopeOpacity }}
          >
            NOPE
          </div>

          {/* Photo */}
          <div
            className="matchus-photo-container"
            onClick={() => {
              if (profile.id === user.id) {
                navigate(`/user/${profile.id}`);
              } else {
                navigate(`/user/profile/${profile.id}`);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={currentPhoto}
              alt={profile.full_name}
              className="matchus-photo"
              draggable={false}
            />

          </div>

          {/* Profile Info Overlay */}
          <div className="matchus-info">
            <div className="matchus-info-header">
              <div className="matchus-name-section">
                <h2 className="matchus-name">
                  {getFirstName(profile.full_name)}, {profile.age}
                  {isOnline(profile.last_active) && (
                    <span className="matchus-online-indicator"></span>
                  )}
                </h2>
                <div className="matchus-location">
                  <FaMapMarkerAlt />
                  <span>{profile.city}{profile.city && profile.country && ', '}{profile.country}</span>
                </div>
              </div>
              <div className="matchus-info-actions">
                <button
                  className="matchus-gift-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowGiftModal(true);
                  }}
                >
                  <FaGift />
                </button>
              <button
                  className="matchus-info-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (profile.id === user.id) {
                    navigate(`/user/${profile.id}`);
                  } else {
                    navigate(`/user/profile/${profile.id}`);
                  }
                }}
              >
                  <FaChevronDown />
              </button>
              </div>
            </div>

            {/* Score & Quick Stats */}
            <div className="matchus-quick-stats">
              <div className="matchus-score-display">
                <div className="matchus-score-circle">
                  <span className="matchus-score-percentage">{profile.score?.percentage || 85}%</span>
                  <div className="matchus-score-ring" style={{
                    background: `conic-gradient(#4ecdc4 ${profile.score?.percentage || 85}%, rgba(255,255,255,0.2) 0deg)`
                  }}>
                  </div>
                </div>
                <div className="matchus-score-info">
                  <div className="matchus-score-label">Match Score</div>
                  <div className="matchus-score-details">
                    {profile.score?.likes || 1250} likes • {profile.score?.total || 1470} total
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Modal Removed - Now navigates to profile page */}
        <button
          className="matchus-modal-close"
          onClick={() => setShowProfileModal(false)}
        >
          ×
        </button>
        </div>
      </div>

        {/* Second Row - Action Buttons */}
        <div className="matchus-buttons-row">
          <div className="matchus-actions-card-bottom">
          <button
          className="matchus-action-btn nope"
            onClick={handleDislike}
          >
            <FaTimes />
          </button>

          <button
          className="matchus-action-btn gift"
            onClick={(e) => {
              e.stopPropagation();
              setShowGiftModal(true);
            }}
          >
            <FaGift />
          </button>

          <button
          className="matchus-action-btn like"
            onClick={handleLike}
          >
            <FaHeart />
          </button>
        </div>
      </div>

          </div>
        )}

      {/* Gift Modal */}
      {showGiftModal && (
        <GiftModal
          isOpen={showGiftModal}
          onClose={() => setShowGiftModal(false)}
          profileId={profile.id}
        />
      )}

      {/* Ads Section */}
      <div className="container-fluid px-4">
        <Ads location="encounters_page" limit={1} />
      </div>
      </div>
    </div>
  );
};

export default Encounters;
